import i18next from 'i18next';
import moment from 'moment';
import { ATS } from '../../../../interfaces';
import { split } from '../../../../utils/array-utils';
import { EpisodeTitle, SeriesTitle, Title } from '../../titles/types/title';
import { Product, Purchase, Rental, SubscribedProduct } from './types';

export function createProducts(products: ATS.ProductForTitle[]): Product[] {
  return products.map((product) => ({
    id: product.id,
    name: product.title,
    type: product.contentType === 'est' ? 'purchase' : 'rental',
    resolution: product.resolution,
    price: product.price,
  }));
}

export function createSubscribedProduct(product: ATS.ProductSubscription): SubscribedProduct {
  if (product.contentType === 'est') {
    return createPurchase(product);
  } else {
    return createRental(product);
  }
}

export function createPurchase(product: ATS.ProductSubscription): Purchase {
  return {
    id: product.productId,
    name: product.name,
    masterId: product.masterId,
    platformTitleId: product.platformTitleId,
    purchasedAt: product.purchaseDate,
    expiresAt: product.expiration,
    type: 'purchase',
    resolution: product.resolution,
    price: product.price,
  };
}

export function createRental(product: ATS.ProductSubscription): Rental {
  return {
    id: product.productId,
    name: product.name,
    masterId: product.masterId,
    platformTitleId: product.platformTitleId,
    purchasedAt: product.purchaseDate,
    expiresAt: product.expiration,
    type: 'rental',
    resolution: product.resolution,
    price: product.price,
  };
}

export function humanizeExpirationDate(expiration: string) {
  const now = moment();
  const expiresAt = moment(expiration);
  const diff = expiresAt.diff(now, 'm');
  const duration = moment.duration(diff, 'm');

  if (duration.asMinutes() < 60) {
    return i18next.t('time:minutes-left', { count: Math.floor(duration.asMinutes()) });
  }

  return i18next.t('time:hours-left', { count: Math.floor(duration.asHours()) });
}

export function getClosestProduct(products: SubscribedProduct[]) {
  if (products.length === 0) {
    return undefined;
  }

  return products.reduce((latestProduct, product) => {
    // Prioritize rentals because they have expiration dates
    if (latestProduct.type === 'rental' && product.type === 'purchase') {
      return latestProduct;
    }

    if (product.type === 'rental') {
      return moment(latestProduct.expiresAt).diff(product.expiresAt) > 0 ? product : latestProduct;
    }

    return latestProduct;
  });
}

export function sortTitlesByProduct(titles: Title[], products: SubscribedProduct[]) {
  return titles.sort((a, b) => {
    const aProduct = products.find((p) => p.masterId === a.masterId);
    const bProduct = products.find((p) => p.masterId === b.masterId);

    if (!aProduct || !bProduct) {
      return -1;
    }

    return moment(bProduct.purchasedAt).diff(moment(aProduct.purchasedAt));
  });
}

export function combineEpisodesIntoSeries(titles: Title[], products: SubscribedProduct[]): Title[] {
  const [episodes, movies] = split(titles, (title): title is EpisodeTitle => title.type === 'episode');

  const sortedTitles = sortTitlesByProduct([...episodes, ...movies], products);
  return sortedTitles.reduce<Title[]>((acc, title) => {
    const alreadyExists = acc.some((t) => {
      if (t.type === 'series' && title.type === 'episode') {
        return t.masterId === title.series.masterId;
      }

      return t.masterId === title.masterId;
    });

    if (alreadyExists) {
      return acc;
    }

    if (title.type === 'episode') {
      const episode = createSeriesTitle(title);
      acc.push(episode);
      return acc;
    }

    acc.push(title);
    return acc;
  }, []);
}

function createSeriesTitle(episode: EpisodeTitle): SeriesTitle {
  return {
    ...episode,
    episodes: [],
    masterId: episode.series.masterId,
    titleName: episode.series.titleName,
    season: null,
    episode: null,
    type: 'series',
  };
}
